<script lang="ts">
import { Image } from '@builder.io/sdk-svelte';
import { stripSiteDomain } from '$lib/utils/url.js';
import Link from '../../atoms/Link/Link.svelte';

interface Client {
    image: Record<string, unknown>;
}

interface Props {
    title?: string;
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    titleSize?: string;
    linkText?: string;
    linkUrl?: string;
    clients?: Client[];
    hideBlock?: boolean;
}

let {
    title = '',
    headingLevel = 'h2',
    titleSize = 'h2',
    linkText = '',
    linkUrl = '',
    clients = [],
    hideBlock = false,
}: Props = $props();

// Transform URL from Builder.io
const transformedLinkUrl = $derived(stripSiteDomain(linkUrl));
const titleSizeClassName = $derived(`title--${titleSize}`);
</script>

{#if !hideBlock}
    <div class="client-logos">
        <div class="intro">
            {#if title}
                <svelte:element this={headingLevel} class="title {titleSizeClassName}">{title}</svelte:element>
            {/if}
            {#if linkText && transformedLinkUrl}
                <Link to={transformedLinkUrl} class="button button--inverse">
                    {#snippet text()}
                        {linkText}
                    {/snippet}
                </Link>
            {/if}
        </div>

        {#each clients as client, index (index)}
            <div class="client">
                <Image {...(client.image as any)} className="logo" />
            </div>
        {/each}
    </div>
{/if}

<style lang="scss">
    @use '/src/scss/global-constants/typography';
    @use 'sass-mq/mq' as *;

    .client-logos {
        display: grid;
        gap: 3.3rem 2.8rem;
        grid-template-columns: repeat(2, 1fr);
        padding-left: var(--layout-pagegrid-margin);
        padding-right: var(--layout-pagegrid-margin);

        @include mq($from: tablet) {
            gap: 4.5rem 7rem;
            grid-template-columns: repeat(5, 1fr);
        }
    }

    .intro {
        grid-column: 1 / -1;
        @include mq($from: tablet) {
            grid-column: 1 / 3;
        }
    }

    .title {
        color: var(--colour-dreamless-sleep);
        margin-bottom: 1.6rem;
        max-width: 12ch;
        text-wrap: balance;

        @include mq($from: tablet) {
            margin-bottom: 2.4rem;
        }

        &--h1   { @include typography.display-xl; }
        &--h1-5 { @include typography.display-hero; }
        &--h2   { @include typography.display-lg; }
        &--h3   { @include typography.display-md; }
        &--h4   { @include typography.heading-lg; }
        &--h5   { @include typography.heading-md; }
    }

    .client-logos :global(.button) {
        display: inline-block;
    }

    .client {
        align-self: center;
        justify-self: center;
    }

    .client-logos :global(.logo) {
        height: 5rem;
        max-width: 100%;
        object-fit: contain !important; // !important is needed to override the default object-fit: cover

        @include mq($from: tablet) {
            height: 7.5rem;
        }
    }
</style>
